// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useShellStore } from '../../stores/shellStore';
import { useAuth } from '../../hooks/useAuth';
import { useRouteSiteId } from '../../hooks/useRouteSiteId';

/**
 * Syncs the site named by the URL into the shell store, for back/forward and
 * deep links. Only a roster site may become the active scope. Rendered below
 * SiteRolloutRoster, so a site the rollout hides is outside the roster too: a
 * link to one lands on Home instead of opening it. It wraps the shells so that
 * decision is made while rendering — an effect would let the linked page mount
 * and start its queries for the hidden site first.
 */
function SiteRouteSync({ children }: { children?: React.ReactNode }) {
  const site_id = useRouteSiteId();
  const selectedSiteId = useShellStore(s => s.selectedSiteId);
  const setSelectedSite = useShellStore(s => s.setSelectedSite);
  const { sites, isLoading, sitesError } = useAuth();

  const routedSite = sites.find(site => site.site_id === site_id);
  // An empty roster is the shell's own empty state, not a bad link.
  const outsideRoster = !!site_id && !routedSite && !isLoading && !sitesError && sites.length > 0;

  // A picker change must not restore the old route's selection (AB#29751).
  // Sync on navigation, or when the routed site first appears in the roster.
  const latest = useRef({ selectedSiteId, routedSite, setSelectedSite });
  latest.current = { selectedSiteId, routedSite, setSelectedSite };

  useEffect(() => {
    const { selectedSiteId: selected, routedSite: site, setSelectedSite: select } = latest.current;
    if (!site || site.site_id === selected) return;
    select(site.site_id, site.label);
  }, [site_id, routedSite?.site_id]);

  if (outsideRoster) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default SiteRouteSync;
